package ru.dinarastepina.fabvocab.web.api

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import ru.dinarastepina.fabvocab.contracts.ApiErrorDto
import ru.dinarastepina.fabvocab.contracts.AuthResponse
import ru.dinarastepina.fabvocab.contracts.ExerciseDto
import ru.dinarastepina.fabvocab.contracts.LoginRequest
import ru.dinarastepina.fabvocab.contracts.LookupWordRequest
import ru.dinarastepina.fabvocab.contracts.RecordExerciseResultRequest
import ru.dinarastepina.fabvocab.contracts.RegisterRequest
import ru.dinarastepina.fabvocab.contracts.SaveWordRequest
import ru.dinarastepina.fabvocab.contracts.SaveWordResponse
import ru.dinarastepina.fabvocab.contracts.WordEntryDto
import ru.dinarastepina.fabvocab.contracts.WordStatsDto

@OptIn(kotlin.uuid.ExperimentalUuidApi::class)
class VocabularyApiClient {
    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    private val token: String
        get() = kotlinx.browser.localStorage.getItem("fabvocab_token") ?: ""

    fun isLoggedIn(): Boolean = token.isNotEmpty()

    private val client = HttpClient(Js) {
        install(ContentNegotiation) {
            json(json)
        }
    }

    private fun HttpRequestBuilder.authHeader() {
        val t = token
        if (t.isNotEmpty()) header("Authorization", "Bearer $t")
    }

    suspend fun register(username: String, password: String): AuthResponse =
        client.post("/api/auth/register") {
            contentType(ContentType.Application.Json)
            setBody(RegisterRequest(username, password))
        }.bodyOrThrow<AuthResponse>().also {
            kotlinx.browser.localStorage.setItem("fabvocab_token", it.token)
        }

    suspend fun login(username: String, password: String): AuthResponse =
        client.post("/api/auth/login") {
            contentType(ContentType.Application.Json)
            setBody(LoginRequest(username, password))
        }.bodyOrThrow<AuthResponse>().also {
            kotlinx.browser.localStorage.setItem("fabvocab_token", it.token)
        }

    fun logout() {
        kotlinx.browser.localStorage.removeItem("fabvocab_token")
    }

    suspend fun getWords(): List<WordEntryDto> =
        client.get("/api/words") { authHeader() }.bodyOrThrow()

    suspend fun getWord(id: String): WordEntryDto? =
        client.get("/api/words/$id") { authHeader() }.let { response ->
            if (response.status.value == 404) null else response.bodyOrThrow()
        }

    suspend fun lookupWord(word: String): WordEntryDto =
        client.post("/api/words/lookup") {
            authHeader()
            contentType(ContentType.Application.Json)
            setBody(LookupWordRequest(word))
        }.bodyOrThrow()

    suspend fun saveWord(word: WordEntryDto): SaveWordResponse =
        client.post("/api/words") {
            authHeader()
            contentType(ContentType.Application.Json)
            setBody(SaveWordRequest(word))
        }.bodyOrThrow()

    suspend fun deleteWord(id: String) {
        client.delete("/api/words/$id") { authHeader() }.throwIfError()
    }

    suspend fun getWordStats(id: String): WordStatsDto =
        client.get("/api/words/$id/stats") { authHeader() }.bodyOrThrow()

    suspend fun getExercises(): List<ExerciseDto> =
        client.get("/api/exercises") { authHeader() }.bodyOrThrow()

    suspend fun recordExerciseResult(wordId: String, exerciseType: String, wasCorrect: Boolean) {
        client.post("/api/exercises/result") {
            authHeader()
            contentType(ContentType.Application.Json)
            setBody(RecordExerciseResultRequest(wordId, exerciseType, wasCorrect))
        }.throwIfError()
    }

    private suspend inline fun <reified T> HttpResponse.bodyOrThrow(): T {
        throwIfError()
        return body()
    }

    private suspend fun HttpResponse.throwIfError() {
        if (status.value in 200..299) return
        val bodyText = bodyAsText()
        val message = runCatching {
            json.decodeFromString<ApiErrorDto>(bodyText).message
        }.getOrDefault(bodyText.ifBlank { "HTTP ${status.value}: ${status.description}" })
        throw IllegalStateException(message)
    }
}
